import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode
} from "react";
import { useAuth } from "../auth/AuthProvider.js";
import { Button, Card, DataTable } from "../components/index.js";
import type { Column } from "../components/index.js";
import type { FbaInventoryItem } from "../types.js";
import { ProductImage } from "../components/ProductImage.js";

/**
 * Inventário FBA (Design.md §8.1). LEITURA do snapshot do Firestore via
 * GET /api/v1/inventory (a SP-API nunca é tocada pelo frontend). A API já EXCLUI
 * ofertas encerradas e enriquece cada item com imagem/título (Listings Items
 * API) e o flag de favorito. O botão "Sincronizar da Amazon" (POST
 * /inventory/sync, ADMIN) enfileira a sync task. Filtros locais (req 5): Todos /
 * Com estoque / Favoritos. A estrela (coluna lateral) favorita via PUT
 * /inventory/favorite.
 */
const NUM = new Intl.NumberFormat("pt-BR");

type Filtro = "all" | "stock" | "fav";

export function InventarioPage(): ReactNode {
  const { api, me } = useAuth();
  const isAdmin = me?.role === "ADMIN";
  const [items, setItems] = useState<FbaInventoryItem[]>([]);
  const [marketplaceId, setMarketplaceId] = useState<string>("");
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("all");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setErro(null);
    try {
      const res = await api.getInventory();
      if (seq !== reqSeq.current) return;
      setItems(res.items);
      setMarketplaceId(res.marketplaceId);
    } catch (error: unknown) {
      if (seq !== reqSeq.current) return;
      setErro(
        error instanceof Error ? error.message : "Falha ao carregar o inventário."
      );
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const sincronizar = useCallback(async () => {
    setSincronizando(true);
    setErro(null);
    setSyncMsg(null);
    try {
      const { settings } = await api.getSettings();
      const sellerId = settings.amazonSellerId.trim();
      if (sellerId.length === 0) {
        setErro(
          "Seller ID não configurado. Defina o Seller ID em Configurações antes de sincronizar."
        );
        return;
      }
      await api.syncInventory(sellerId, settings.activeMarketplaceId);
      setSyncMsg(
        "Sincronização solicitada à Amazon (assíncrona). Aguarde alguns segundos e clique em \"Atualizar\"."
      );
    } catch (error: unknown) {
      setErro(
        error instanceof Error ? error.message : "Falha ao sincronizar o inventário."
      );
    } finally {
      setSincronizando(false);
    }
  }, [api]);

  const toggleFavorite = useCallback(
    async (item: FbaInventoryItem) => {
      const next = !item.favorite;
      // Otimista: atualiza já; reverte se a API falhar.
      setItems((prev) =>
        prev.map((x) =>
          x.sellerSku === item.sellerSku ? { ...x, favorite: next } : x
        )
      );
      try {
        await api.setInventoryFavorite(
          item.sellerSku,
          next,
          marketplaceId.length > 0 ? marketplaceId : undefined
        );
      } catch (error: unknown) {
        setItems((prev) =>
          prev.map((x) =>
            x.sellerSku === item.sellerSku ? { ...x, favorite: item.favorite } : x
          )
        );
        setErro(
          error instanceof Error ? error.message : "Falha ao atualizar o favorito."
        );
      }
    },
    [api, marketplaceId]
  );

  const visible = useMemo(() => {
    const term = busca.trim().toLowerCase();
    return items.filter((it) => {
      if (filtro === "stock" && it.fulfillableQuantity <= 0) return false;
      if (filtro === "fav" && !it.favorite) return false;
      if (term.length === 0) return true;
      return (
        it.sellerSku.toLowerCase().includes(term) ||
        it.asin.toLowerCase().includes(term) ||
        it.productName.toLowerCase().includes(term)
      );
    });
  }, [items, busca, filtro]);

  const favCount = useMemo(() => items.filter((i) => i.favorite).length, [items]);
  const stockCount = useMemo(
    () => items.filter((i) => i.fulfillableQuantity > 0).length,
    [items]
  );

  const columns: readonly Column<FbaInventoryItem>[] = [
    {
      key: "fav",
      header: "★",
      width: 40,
      align: "center",
      render: (it) => (
        <StarButton
          active={it.favorite}
          label={it.sellerSku}
          onToggle={() => void toggleFavorite(it)}
        />
      )
    },
    {
      key: "image",
      header: "Imagem",
      width: 56,
      render: (it) => <ProductImage src={it.imageUrl} label={it.productName || it.sellerSku} />
    },
    {
      key: "sku",
      header: "SKU",
      render: (it) => <span className="mono">{it.sellerSku}</span>
    },
    {
      key: "asin",
      header: "ASIN",
      render: (it) => <span className="mono">{it.asin}</span>
    },
    {
      key: "name",
      header: "Descrição",
      render: (it) => it.productName || "—"
    },
    {
      key: "stock",
      header: "Qtd em Estoque",
      align: "right",
      render: (it) => NUM.format(it.fulfillableQuantity)
    },
    {
      key: "shipped",
      header: "Qtd Enviada",
      align: "right",
      render: (it) => NUM.format(it.inboundShippedQuantity)
    }
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap"
        }}
      >
        <div>
          <h2 style={{ fontSize: 21, fontWeight: 700 }}>Inventário</h2>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: "0.2rem" }}>
            Estoque FBA · Amazon UK · somente ofertas ativas
            {marketplaceId.length > 0 ? (
              <span className="mono" style={{ marginLeft: 6 }}>
                ({marketplaceId})
              </span>
            ) : null}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            {loading ? "Atualizando…" : "Atualizar"}
          </Button>
          {isAdmin ? (
            <Button onClick={() => void sincronizar()} disabled={sincronizando}>
              {sincronizando ? "Sincronizando…" : "Sincronizar da Amazon"}
            </Button>
          ) : null}
        </div>
      </header>
      {syncMsg !== null ? (
        <p role="status" style={{ fontSize: 12.5, color: "var(--blue)" }}>
          {syncMsg}
        </p>
      ) : null}

      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.85rem",
            marginBottom: "0.85rem",
            flexWrap: "wrap"
          }}
        >
          <div role="group" aria-label="Filtrar inventário" style={{ display: "flex", gap: "0.4rem" }}>
            <FilterChip active={filtro === "all"} onClick={() => setFiltro("all")}>
              Todos ({items.length})
            </FilterChip>
            <FilterChip active={filtro === "stock"} onClick={() => setFiltro("stock")}>
              Com estoque ({stockCount})
            </FilterChip>
            <FilterChip active={filtro === "fav"} onClick={() => setFiltro("fav")}>
              ★ Favoritos ({favCount})
            </FilterChip>
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ maxWidth: 320, flex: "1 1 220px" }}>
            <input
              type="search"
              placeholder="Pesquisar por SKU, ASIN ou descrição"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{ width: "100%" }}
              aria-label="Pesquisar inventário"
            />
          </div>
        </div>

        {erro !== null ? (
          <p role="alert" style={{ color: "var(--orange)", fontSize: 12.5, marginBottom: "0.5rem" }}>
            {erro}
          </p>
        ) : null}

        <DataTable
          columns={columns}
          rows={visible}
          getRowKey={(it) => it.id}
          empty={
            loading
              ? "Carregando…"
              : items.length === 0
                ? "Nenhum item no inventário."
                : filtro === "fav"
                  ? "Nenhum produto favorito. Clique na estrela para favoritar."
                  : "Nenhum item corresponde ao filtro."
          }
        />
      </Card>
    </div>
  );
}

function StarButton({
  active,
  label,
  onToggle
}: {
  active: boolean;
  label: string;
  onToggle: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      aria-label={active ? `Desfavoritar ${label}` : `Favoritar ${label}`}
      title={active ? "Desfavoritar" : "Favoritar"}
      style={{
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: 18,
        lineHeight: 1,
        color: active ? "var(--orange)" : "var(--text-faint)"
      }}
    >
      {active ? "★" : "☆"}
    </button>
  );
}

function FilterChip({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  const style: CSSProperties = {
    border: `1px solid ${active ? "var(--orange)" : "var(--border)"}`,
    background: active ? "var(--orange-soft)" : "var(--panel)",
    color: active ? "var(--orange)" : "var(--text-muted)",
    fontWeight: active ? 700 : 500,
    fontSize: 12,
    borderRadius: 20,
    padding: "0.35rem 0.85rem",
    cursor: "pointer"
  };
  return (
    <button type="button" onClick={onClick} aria-pressed={active} style={style}>
      {children}
    </button>
  );
}
